import StyleInjector from "app/utils/style-injector";
import ScriptInjector from "app/utils/ScriptInjector";
import LoadTimer from "app/utils/load-timer";
import Resources from "build/resources";
import Log from "app/utils/log";
import ErrorReport from "app/utils/error-report";
import * as IXScript from "app/utils/ix/IXScript";
import SiteType from "types/SiteType";
import * as ProjectorDefaults from "app/lib/ProjectorDefaults";
import BlockOverlayComponent, { BlockType } from "app/components/block-overlay/BlockOverlay";

const scriptInjector = ScriptInjector.getInstance();
const log = new Log("DependencyLoader");
const IMA_SDK = "//imasdk.googleapis.com/js/sdkloader/ima3.js";
const IMA_DAI_SDK = "//imasdk.googleapis.com/js/sdkloader/ima3_dai.js";
const SPRITZ = "https://assets.bwbx.io/s3/spritz/v1.0.0/spritz.js";

class DependencyLoader {

    loader: any;
    deferQueue: any[] | null;
    site: SiteType;
    blockRenderer: BlockOverlayComponent;

    constructor(site: SiteType) {
        this.site = site;
        this.loader = null;
        this.deferQueue = [];
        this.blockRenderer = new BlockOverlayComponent();
    }

    /**
     *
     * @param pipeFn Function that is injected into a pipeline before instructions are executed
     * @param mustLoadAdDependencies
     */
    loadDependencies(pipeFn = () => {}, mustLoadAdDependencies = true): Promise<any> {
        if (this.loader) {
            return this.loader
                .then(pipeFn)
                .catch(error => ErrorReport.recordGenericError(log, error));
        }

        this.loader = this._loadAppDependencies(mustLoadAdDependencies)
            .then(() => this._loadAppBundle())
            .then(pipeFn)
            .then((passThrough) => {
                this.loader = null;
                this.executeDeferredInstructions();
                return passThrough;
            })
            .catch(error => ErrorReport.recordGenericError(log, error));

        return this.loader;
    }

    /**
     * @method
     * @param {string} method
     * @param {array} args
     */
    deferExecution(method, args) {
        if (this.deferQueue) {
            this.deferQueue.push({ method, args });
        }
    }

    /**
     * @method
     */
    executeDeferredInstructions() {
        if (!this.deferQueue) {
            return;
        }

        this.deferQueue.forEach((instruction) => {
            this[instruction.method].apply(this, instruction.args);
        });

        this.deferQueue.length = 0;
    }

    /**
     * @method
     */
    dispose() {
        this.loader = this.deferQueue = null;
    }

    renderBlockOverlay(blockType: BlockType) {
        this.blockRenderer.render(this._getHtmlContainer(), blockType);
    }

    _loadAppDependencies(mustLoadAdDependencies = true) {
        const ixScriptUrl = IXScript.getScriptUrl(this.site);
        const dependencies = [
            StyleInjector.inject(fixUrl(Resources.APP_CSS), this._getHtmlChildId()),
            scriptInjector.inject(fixUrl(Resources.VENDOR_BUNDLE), this._getHtmlChildId(), LoadTimer.loadTypes.VENDOR_BUNDLE)
        ];

        if (mustLoadAdDependencies) {
            dependencies.push(...[
                scriptInjector.inject(IMA_SDK, this._getHtmlChildId(), LoadTimer.loadTypes.IMA_SDK)
                    .catch((error) => {
                        this.renderBlockOverlay("ad-blocker");
                        throw error;
                    }),
                scriptInjector.inject(IMA_DAI_SDK, this._getHtmlChildId(), LoadTimer.loadTypes.IMA_DAI_SDK).catch((error) => {
                    this.renderBlockOverlay("ad-blocker");
                    throw error;
                })
            ]);

            if (ProjectorDefaults.isIndexExchangeEnabled()) {
                pushIXToSpritzCmdQueue(ixScriptUrl);
                dependencies.push(scriptInjector
                    .inject(SPRITZ, this._getHtmlChildId(), LoadTimer.loadTypes.SPRITZ, { timeout: 3000 })
                    .catch(() => {}));
            }
        }

        return Promise.all(dependencies);
    }

    /**
     * @private
     */
    _loadAppBundle() {
        return scriptInjector.inject(fixUrl(Resources.APP_BUNDLE), this._getHtmlChildId(), LoadTimer.loadTypes.APP_BUNDLE);
    }

    /**
     * Overriden by child class
     */
    _getHtmlChildId(): undefined | string {
        return undefined;
    }

    /**
     * Overriden by child class
     */
    _getHtmlContainer(): HTMLElement | null {
        return null;
    }
}

function pushIXToSpritzCmdQueue(ixScriptUrl: string) {
    // Use Spritz to load IX script to avoid potential race condition with bb.ads
    window.Spritz = window.Spritz || {};
    window.Spritz.cmd = window.Spritz.cmd || [];
    window.Spritz.cmd.push(function() {
        window.Spritz.inject(ixScriptUrl);
    });
}

function fixUrl(url) {
    if (location.protocol === "https:" && Resources.isDev) {
        return url.replace(`:${ Resources.localPort }`, `:${ Resources.localPort + 1 }`);
    }

    return url;
}

export default DependencyLoader;
